import { ChangeDetectorRef, Component, effect, inject, OnInit, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterOutlet, RoutesRecognized, NavigationEnd } from '@angular/router';
import { SplashScreenComponent } from './layout/splash-screen/splash-screen.component';
import { isPlatformBrowser } from '@angular/common';
import { ThemeService } from './shared/theme.service';
import { TranslationService } from './shared/translation.service';
import { AuthService } from './auth/auth.service';
import { UserSettingService } from './services/user-setting.service';
import { SharedService } from './shared/services/shared.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ChatService } from './services/chat/chat.service';
import { filter, pairwise, take } from 'rxjs';
import { ToasterService } from './shared/services/toaster.service';
import { LiveSessionService } from './services/live-session.service';
import { environment } from '../environments/environment';
import { User } from './model/user.model';

declare var google: { maps: { LatLng: new (lat: number, lng: number) => unknown; Geocoder: new () => { geocode: (req: Record<string, unknown>, cb: (results: unknown[], status: string) => void) => void }; GeocoderStatus: { OK: string } } };
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-root',
    imports: [
    RouterOutlet,
    SplashScreenComponent
],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'Golf.ai';
  isLoading = true;
  isLoggedIn = false;
  // translations: { [key: string]: string } = {};
  private themeService = inject(ThemeService);
  private translationService = inject(TranslationService);
  private _authService = inject(AuthService);
  private _settingService = inject(UserSettingService);
  private _sharedService = inject(SharedService);
  private _chatService = inject(ChatService);
  private _router = inject(Router);
  private platformId = inject<object>(PLATFORM_ID);
  private cd = inject(ChangeDetectorRef);
  private _toaster = inject(ToasterService);
  private _translationService = inject(TranslationService);
  private _liveSessionService = inject(LiveSessionService);

  constructor() {
    effect(() => {
      this.isLoading = this._sharedService.loading();
      this.cd.detectChanges();
    });
    effect(() => {
      this.isLoggedIn = this._sharedService.loggedIn();
      if(this.isLoggedIn && !this._sharedService.userSettingsLoaded()){
        this._sharedService.userSettingsLoaded.set(true);
        this.getUserSetting();
      }
    });
  }
  ngOnInit(): void {
    // subscribe router event
    this._router.events.pipe(filter((evt: unknown) => evt instanceof RoutesRecognized), pairwise())
    .subscribe((events: RoutesRecognized[]) => {
      this._sharedService.setPreviousUrl(events[0].urlAfterRedirects);
    });
    
    // Add scroll to top functionality on route changes
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this._liveSessionService.newPageView();
      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          window.scrollTo(0, 0);
          const leftBlock = document.querySelector('.left_block');
          if (leftBlock) {
            leftBlock.scrollTo(0, 0);
          }
        }, 100);
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      if(this._sharedService.getLocalStorage('golfTap')){
        this._sharedService.golfTap();
      }
      this.themeService.isDarkTheme.subscribe((isDarkTheme) => {
        if (isDarkTheme) {
          document.body.classList.add('dark-theme');
        } else {
          document.body.classList.remove('dark-theme');
        }
      });

      // Cookie-based auth: ask the API whether this session is signed in.
      // If not authenticated, pre-fetch /sso/url so the Login button has it
      // ready — but don't redirect automatically.
      // We keep the splash screen up until we know: signed-in → also wait for
      // user settings; signed-out / error → hide it as soon as the check resolves.
      this._authService.checkAuthStatus().pipe(take(1)).subscribe({
        next: (authenticated) => {
          this.isLoggedIn = authenticated;
          this._sharedService.loggedIn.set(authenticated);
          if (authenticated) {
            if (!this._sharedService.userSettingsLoaded()) {
              this._sharedService.userSettingsLoaded.set(true);
              this.getUserSetting();
            }
            return;
          }
          this._sharedService.loading.set(false);
          this._authService.fetchSsoUrl().pipe(take(1)).subscribe();
        },
        error: () => {
          // Treat auth-check failures as "not signed in" so the splash hides
          // and the user can see the page (Login button shows in the header).
          this._sharedService.loading.set(false);
        },
      });
    }
  }
  
  //  Commenting here as we are calling the same in the header component

  getUserSetting() {
    this._settingService.loadUserWithMedia().pipe(take(1)).subscribe({
      next: (user) => {
        this._liveSessionService.identifyUser(user.name, user.email, environment.envName);
        this.identifyMixpanel(user);
        this._translationService.setLanguage(user.language?.code || 'en');
        this._sharedService.loading.set(false);
        this._sharedService.loggedIn.set(true);
      },
      error: (error) => {
        this._toaster.error('Error fetching user settings:', error);
        this._sharedService.loading.set(false);
      }
    });
  }
  
  
  setLanguage(lang: string): void {
    this.translationService.setLanguage(lang);
  }

  getReverseGeocodingData(lat: number, lng: number): void {
    if (google){
      const latLong = new google.maps.LatLng(lat, lng);
      // This is making the Geocode request
      const geoCoder = new google.maps.Geocoder();
      geoCoder.geocode({ latLng: latLong },  (results: unknown[], status: string) => {
          if (status !== google.maps.GeocoderStatus.OK) {
              alert(status);
          }
          // This is checking to see if the Geoeode Status is OK before proceeding
          // if (status === google.maps.GeocoderStatus.OK) {
          //   if (results.length > 3){
          //     this.sharedService.location = results[3].formatted_address;
          //   } else if (results.length > 2){
          //     this.sharedService.location = results[2].formatted_address;
          //   } else {
          //     this.sharedService.location = results[1].formatted_address;
          //   }
          // }
      });
    }
  }

    private identifyMixpanel(user: User) {
    try {
      if (!isPlatformBrowser(this.platformId)) return;
      const w = window as unknown as Record<string, unknown>;
      if (!w || !w['mixpanel'] || !user) return;
      if (!user.email) return;
      const aliasKey = `mp_alias_${user.email}`;
      const mp = w['mixpanel'] as Record<string, unknown>;
      if (!localStorage.getItem(aliasKey)) {
        try { (mp['alias'] as (id: string) => void)(user.email); } catch (_) {}
        localStorage.setItem(aliasKey, '1');
      }
      try { (mp['identify'] as (id: string) => void)(user.email); } catch (_) {}
      const people = mp['people'] as Record<string, unknown> | undefined;
      if (people && typeof people['set'] === 'function') {
        try {
          (people['set'] as (props: Record<string, unknown>) => void)({
            $email: user.email,
            $name: user.name,
          });
        } catch (_) {}
      }
    } catch (_) {}
  }

}
